/**
 * TNK-132 — Account & Security sub-page. Houses passkey management.
 * Used to live as an inline section on /settings; folded into its
 * own sub-page so the landing reads as a tile grid.
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, ShieldCheck } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import {
  fetchPasskeys,
  registerPasskey,
  deletePasskey,
  type PasskeyRow,
} from "@/features/passkey";

export const Route = createFileRoute("/settings/account-security")({
  component: AccountSecurityPage,
});

function AccountSecurityPage() {
  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/settings" }} label="Settings" />
      </div>

      <div className="border-b border-border pb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-bold text-foreground">
            Account &amp; Security
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Passkeys — phishing-resistant sign-in that works independently of
          eBay.
        </p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-1">
        <PasskeysSection />
      </div>
    </section>
  );
}

function PasskeysSection() {
  const [rows, setRows] = useState<PasskeyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deviceName, setDeviceName] = useState("");

  const refresh = useCallback(async () => {
    try {
      setRows(await fetchPasskeys());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load passkeys.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const add = async () => {
    setError(null);
    setBusy(true);
    try {
      await registerPasskey(deviceName.trim());
      setDeviceName("");
      await refresh();
    } catch (e) {
      if (e instanceof DOMException && e.name === "NotAllowedError") return;
      setError(e instanceof Error ? e.message : "Could not add passkey.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setError(null);
    try {
      await deletePasskey(id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove passkey.");
    }
  };

  return (
    <section className="space-y-3 rounded-md border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Passkeys</h2>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Add a passkey to sign in without eBay — a phishing-resistant backup if
        you ever lose eBay access. eBay and your passkeys both unlock this same
        account.
      </p>

      {rows && rows.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {rows.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 p-2.5"
            >
              <div className="min-w-0">
                <div className="truncate text-sm text-foreground">
                  {p.deviceName || "Unnamed passkey"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Added {new Date(p.createdAt).toLocaleDateString()}
                  {p.lastUsedAt
                    ? ` · last used ${new Date(p.lastUsedAt).toLocaleDateString()}`
                    : " · never used"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void remove(p.id)}
                className="shrink-0 rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-destructive"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {rows && rows.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          No passkeys registered yet.
        </p>
      )}

      <div className="flex items-end gap-2">
        <label className="flex-1 space-y-1">
          <span className="text-[11px] font-medium text-muted-foreground">
            Device name (optional)
          </span>
          <input
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder="e.g. MacBook, iPhone"
            className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
        </label>
        <button
          type="button"
          onClick={() => void add()}
          disabled={busy}
          className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Waiting…" : "Add passkey"}
        </button>
      </div>

      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </section>
  );
}
